import { enableMapSet } from "immer";
import { create, type StateCreator } from "zustand";
import { immer } from "zustand/middleware/immer";

import type { Frame } from "@/types/dragon-tiles";

enableMapSet();

export type AssignMode = "move" | "copy";

export interface AssignTargetOption {
  innerTileId: string;
  label: string;
}

export interface PendingAssign {
  targetInnerTileId: string;
  targetLabel: string;
  frames: Frame[];
  sequenceFrameCount: number;
}

interface ParentTileViewStore {
  selectedAudienceId: string | null;

  isAssignMode: boolean;
  assignMode: AssignMode;
  assignSourceInnerTileId: string | null;
  assignTargetInnerTileId: string | null;
  assignTargets: AssignTargetOption[];
  selectedFrameIds: Set<string>;
  pendingAssign: PendingAssign | null;

  actions: {
    toggleAudience: (audienceId: string) => void;
    setSelectedAudienceId: (audienceId: string | null) => void;
    clearSelection: () => void;

    enterAssignMode: (
      sourceInnerTileId: string,
      targets: AssignTargetOption[],
    ) => void;
    exitAssignMode: () => void;
    setAssignMode: (mode: AssignMode) => void;
    setAssignTarget: (innerTileId: string) => void;
    toggleFrameSelection: (frameId: string) => void;
    openMoveOrCopyConfirm: (pending: PendingAssign) => void;
    closeMoveOrCopyConfirm: () => void;
  };
}

export const parentTileViewStore = () => {
  const slice: StateCreator<ParentTileViewStore, [["zustand/immer", never]]> = (
    set,
  ) => ({
    selectedAudienceId: null,
    isAssignMode: false,
    assignMode: "move",
    assignSourceInnerTileId: null,
    assignTargetInnerTileId: null,
    assignTargets: [],
    selectedFrameIds: new Set<string>(),
    pendingAssign: null,
    actions: {
      toggleAudience(audienceId) {
        set((state) => {
          state.selectedAudienceId =
            state.selectedAudienceId === audienceId ? null : audienceId;
        });
      },
      setSelectedAudienceId(audienceId) {
        set((state) => {
          state.selectedAudienceId = audienceId;
        });
      },
      clearSelection() {
        set((state) => {
          state.selectedAudienceId = null;
          state.isAssignMode = false;
          state.assignSourceInnerTileId = null;
          state.assignTargetInnerTileId = null;
          state.assignTargets = [];
          state.selectedFrameIds = new Set();
          state.pendingAssign = null;
        });
      },
      enterAssignMode(sourceInnerTileId, targets) {
        set((state) => {
          state.isAssignMode = true;
          state.assignMode = "move";
          state.assignSourceInnerTileId = sourceInnerTileId;
          state.assignTargetInnerTileId = null;
          state.assignTargets = targets;
          state.selectedFrameIds = new Set();
          state.pendingAssign = null;
        });
      },
      exitAssignMode() {
        set((state) => {
          state.isAssignMode = false;
          state.assignSourceInnerTileId = null;
          state.assignTargetInnerTileId = null;
          state.assignTargets = [];
          state.selectedFrameIds = new Set();
          state.pendingAssign = null;
        });
      },
      setAssignMode(mode) {
        set((state) => {
          state.assignMode = mode;
        });
      },
      setAssignTarget(innerTileId) {
        set((state) => {
          state.assignTargetInnerTileId = innerTileId;
        });
      },
      toggleFrameSelection(frameId) {
        set((state) => {
          if (state.selectedFrameIds.has(frameId)) {
            state.selectedFrameIds.delete(frameId);
          } else {
            state.selectedFrameIds.add(frameId);
          }
        });
      },
      openMoveOrCopyConfirm(pending) {
        set((state) => {
          state.pendingAssign = pending;
        });
      },
      closeMoveOrCopyConfirm() {
        set((state) => {
          state.pendingAssign = null;
        });
      },
    },
  });

  const useStore = create<ParentTileViewStore>()(immer(slice));
  const init = useStore.getInitialState();

  return {
    testing: {
      getState: () => useStore.getState(),
    },
    actions: init.actions,

    useSelectedAudienceId: () => useStore((state) => state.selectedAudienceId),
    useIsAssignMode: () => useStore((state) => state.isAssignMode),
    useAssignMode: () => useStore((state) => state.assignMode),
    useAssignSourceInnerTileId: () =>
      useStore((state) => state.assignSourceInnerTileId),
    useIsAssignSource: (innerTileId: string) =>
      useStore(
        (state) =>
          state.isAssignMode && state.assignSourceInnerTileId === innerTileId,
      ),
    useAssignTargetInnerTileId: () =>
      useStore((state) => state.assignTargetInnerTileId),
    useAssignTargets: () => useStore((state) => state.assignTargets),
    useSelectedFrameCount: () =>
      useStore((state) => state.selectedFrameIds.size),
    useIsFrameSelected: (frameId: string) =>
      useStore((state) => state.selectedFrameIds.has(frameId)),
    getSelectedFrameIds: () => useStore.getState().selectedFrameIds,
    usePendingAssign: () => useStore((state) => state.pendingAssign),
  };
};

export const ParentTileViewStore = parentTileViewStore();
